/*
 * The Store contains scenes and faces.  It has several responsibilities:
 *  1) interface with Collection.localStorage
 *  2) merge other stores (unless data's modified date is older)
 *  3) tell anyone listening when a new face is being used
 *
 *  Scenes have their own copy of faces, but having them listed centrally in the store
 *  allows for the following:
 *  1) Compression of multiple scenes together
 *  2) Keeping track of when a new face is used or when the last instance of a face is deleted
 *
 *  There is the potential for conflicting data or abuse since a scene can be copied and contain
 *  incorrect data. This is how this is managed:
 *    1) Downloaded scenes stay in their own "sandbox".
 *    2) Delete face modified times from downloaded scenes
 *    2) You can only use faces from the store's dictionary, not the scene's. If there
 *       are conflicts, the scene's will get overwritten.
 *    3) When you edit a downloaded scene (by creating a new scene you own), we add the
 *       scene's faces, but only if they don't conflict with your existing ones.
*/
import stampit from 'stampit'
import EventEmitter from 'events'

const Collection = stampit({
  name: 'Collection',
  props: {
    prefix: '',
    chats: {},
    faces: {}

  },
  init ({chats, faces, prefix}) {
    this.chats = chats || {}
    this.faces = faces || {}
    this.prefix = prefix || this.prefix
  },
  statics: {
    eventEmitter: new EventEmitter(),
    localStorage: {
      getItem: function (key) {
        try {
          return localStorage.getItem(key)
        } catch (e) {
          return null
        }
      },
      setItem: function (key, data) {
        try {
          return localStorage.setItem(key, data)
        } catch (e) {
          return null
        }
      },
      removeItem: function (key) {
        try {
          return localStorage.removeItem(key)
        } catch (e) {
          return null
        }
      }
    }
  },
  methods: {

    _saveObjectsToStorage (keyWPrefix, dict) {
      this._saveIDsToStorage(keyWPrefix, dict)
      for (const id in dict) {
        if (dict.hasOwnProperty(id)) {
          Collection.localStorage.setItem(this.prefix + id, JSON.stringify(dict[id]))
        }
      }
    },
    _saveIDsToStorage (keyWPrefix, dict) {
      var prefixedIDs = []
      for (const id in dict) {
        if (dict.hasOwnProperty(id)) {
          prefixedIDs.push(this.prefix + id)
        }
      }
      Collection.localStorage.setItem(keyWPrefix, JSON.stringify(prefixedIDs))
    },
    _removeIDsFromStorage (keyWPrefix, dict) {
      var keys = Collection.localStorage.getItem(keyWPrefix)
      if (!keys) {
        // console.warn('Nothing to remove from storage: ' + keyWPrefix)
        return
      }
      keys = JSON.parse(keys)
      if (!Array.isArray(keys)) {
        throw new Error('unexpected format in storage')
      }
      keys.forEach(id => {
        Collection.localStorage.removeItem(id)
      })
      var prefixedIDs = []
      for (const id in dict) {
        if (dict.hasOwnProperty(id)) {
          Collection.localStorage.setItem(this.prefix + id, JSON.stringify(dict[id]))
          prefixedIDs.push(this.prefix + id)
        }
      }
      Collection.localStorage.setItem(keyWPrefix, JSON.stringify(prefixedIDs))
    },

    removeFromStorage () {
      this._removeIDsFromStorage(this.prefix + 'chats', this.chats)
      this._removeIDsFromStorage(this.prefix + 'faces', this.faces)
    },
    saveToStorage () {
      if (typeof Storage === 'undefined') {
        throw new Error('Local Storage not supported')
      }
      this._saveObjectsToStorage(this.prefix + 'chats', this.chats)
      this._saveObjectsToStorage(this.prefix + 'faces', this.faces)
    },
    _loadDictFromStorage (keyWPrefix, dict) {
      var ids = Collection.localStorage.getItem(keyWPrefix)
      var array = []
      if (ids) {
        array = JSON.parse(ids)
      }
      array.forEach(idWPrefix => {
        var str = Collection.localStorage.getItem(idWPrefix)
        if (str) {
          var obj = JSON.parse(str)
          if (idWPrefix.startsWith(this.prefix)) {
            var id = idWPrefix.substring(this.prefix.length)
            // dict[id] = Class(obj)
            dict[id] = obj
          }
        }
      })
    },
    loadFromStorage () {
      if (typeof Storage === 'undefined') {
        throw new Error('Local Storage not supported')
      }

      this._loadDictFromStorage(this.prefix + 'faces', this.faces)
      this._loadDictFromStorage(this.prefix + 'chats', this.chats)
    },
    updateFace (id, face) {
      this.faces[id] = face
      Collection.localStorage.setItem(this.prefix + id, JSON.stringify(face))
      this._saveIDsToStorage(this.prefix + 'faces', this.faces)
    },
    updateChat (id, chat) {
      this.chats[id] = chat
      Collection.localStorage.setItem(this.prefix + id, JSON.stringify(chat))
      this._saveIDsToStorage(this.prefix + 'chats', this.chats)
    },
    deleteChat (id) {
      if (id in this.chats) {
        delete this.chats[id]
      }
      Collection.localStorage.removeItem(this.prefix + id)
      this._saveIDsToStorage(this.prefix + 'chats', this.chats)
    },
    deleteFace (faceId) {
      delete this.faces[faceId]
      Collection.localStorage.removeItem(this.prefix + faceId)
      this._saveIDsToStorage(this.prefix + 'faces', this.faces)
    }
  }
})
export default Collection



// WEBPACK FOOTER //
// ./src/tts/collection.js